'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'
import { HorizontalScroll } from '../blocks/HorizontalScroll'

import * as React from 'react'
import { RetailerMark } from '@/components/homepage-2026/retailer-mark'
import { ProductRail } from '../blocks/Rail'
import { DailyDate } from '../blocks/DailyDate'
import { RotationControls, useSwipeSelection } from '../blocks/RotationControls'
import {
  ArrowLeft,
  BadgeDollarSign,
  Calculator,
  ChartLine,
  ChevronDown,
  CircleCheck,
  Eye,
  Hourglass,
  LayoutPanelTop,
  SlidersHorizontal,
  Timer,
  Leaf,
  ListChecks,
  RotateCcw,
  Ruler,
  ShieldAlert,
  Snowflake,
  TrendingDown,
  Wrench,
  Zap,
  type LucideIcon,
} from '@/components/homepage-october/lucide'

import { Badge } from '@/components/homepage-october/ui/badge'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import type { ChartConfig } from '@/components/ui/chart'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/homepage-october/ui/item'
import { Progress } from '@/components/ui/progress'
import { TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/homepage-october/ui/table'
import { TableScrollHint } from '@/components/ui/table-scroll-hint'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/homepage-october/ui/tabs'
import { ToggleGroup, ToggleGroupItem } from '@/components/homepage-october/ui/toggle-group'
import { cn, formatPrice } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { LiveDot, Section, SectionHeader, words } from '../blocks/layout'
import { ProductRow, shopItem } from '../blocks/ProductCard'
import { useIsClient, useReducedMotion } from '../hooks'
import type { Model, RetailRow } from '../types'
import type { ChartData, Pt } from './IntelChart'

/* ------------------------------------------------------------------ shared helpers */

/**
 * shadcn's <Table> markup, plus the scroll-container contract v2 carried: a named, focusable region, so a keyboard
 * can scroll a table that is wider (or taller) than its card. The rows use shadcn's TableHeader/TableRow/TableHead/TableCell.
 */
function ScrollTable({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <HorizontalScroll label={label} className={className}>
      <table className={compactClassName("w-full caption-bottom text-sm")}>
        {children}
      </table>
    </HorizontalScroll>
  )
}

const DAY = 86_400_000
const isoMs = (iso: string) => Date.parse(iso + 'T00:00:00Z')
const mdy = (iso: string) => {
  const [y, mo, d] = iso.split('-')
  return `${mo}/${d}/${y}`
}
const usd = (cents: number) => formatPrice(cents / 100)
const num = (price: string) => Number(price.replace(/[^0-9.]/g, ''))

/** The lowest row of a retailer list (the model flags it with `best` when it knows; otherwise the cheapest). */
function bestRow(rows: RetailRow[]) {
  const flagged = rows.find((r) => r.best)
  if (flagged) return flagged
  let low: RetailRow | undefined
  for (const r of rows) if (!low || num(r.price) < num(low.price)) low = r
  return low
}

/* ------------------------------------------------------------------ (1) live price check */

function LiveCheck({ m }: { m: Model['intel']['live'] }) {
  const [tab, setTab] = React.useState('0')
  const [picked, setPicked] = React.useState(false)
  const card = React.useRef<HTMLDivElement>(null)
  const hold = React.useRef({ hover: false, focus: false, visible: false })
  const move = (direction: number) => { setPicked(true); setTab((v) => String((Number(v) + direction + m.items.length) % m.items.length)) }
  const swipe = useSwipeSelection(move, () => setPicked(true))
  const reduced = useReducedMotion()
  const n = m.items.length
  const headId = React.useId()
  React.useEffect(() => {
    if (!card.current) return
    const observer = new IntersectionObserver(([entry]) => { hold.current.visible = entry.isIntersecting }, { threshold: 0.15 })
    observer.observe(card.current)
    return () => observer.disconnect()
  }, [])
  // Steps through the three models like v2's rotating panel; pauses on hover and keyboard focus, stops for good once
  // a shopper picks or swipes one, and never runs under reduced motion.
  React.useEffect(() => {
    if (reduced || picked || n < 2) return
    const t = window.setInterval(() => {
      if (hold.current.visible && !hold.current.hover && !hold.current.focus && !document.hidden) setTab((v) => String((Number(v) + 1) % n))
    }, 6500)
    return () => window.clearInterval(t)
  }, [reduced, picked, n])
  // v2's spread line opens with a "↓" glyph: kept as the line's (decorative) mark so the text stays exactly the model's.
  const lead = /^\s*([↓▼])\s*/.exec(m.spread)
  const spread = lead ? m.spread.slice(lead[0].length) : m.spread
  return (
    <Card
      ref={card}
      {...swipe}
      data-auto={6500}
      data-rotator="prices"
      data-paused={picked || reduced}
      className="gap-0! overflow-hidden py-0! shadow-[0_24px_60px_-30px_rgba(4,105,240,0.45)]! ring-1 ring-black/5"
      onMouseEnter={() => { hold.current.hover = true }}
      onMouseLeave={() => { hold.current.hover = false }}
      onFocusCapture={() => { hold.current.focus = true }}
      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) hold.current.focus = false }}
    >
      <div className={compactClassName("relative overflow-hidden border-b bg-gradient-to-br from-white via-[#f5f9ff] to-[#e6f0ff] p-5 md:p-6")}>
        <div aria-hidden="true" className={compactClassName("pointer-events-none absolute -top-20 -right-12 size-56 rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.16),transparent_65%)]")} />
        <p className={compactClassName("relative flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-neutral-600")}>
          <LiveDot />
          {m.label}
        </p>
        <p className={compactClassName("relative mt-3 text-[15px] font-semibold leading-snug text-neutral-900")}>
          <span className={compactClassName("text-[#0454c0]")}>{m.brand}</span> {m.title}
        </p>
        <div className={compactClassName("relative mt-2 flex flex-wrap items-center gap-x-4 gap-y-2")}>
          <p className={compactClassName("tabular text-4xl font-bold tracking-tight text-neutral-950 md:text-5xl")}><span className={compactClassName("pi-price")}>{m.price}</span></p>
          <p className={compactClassName("inline-flex items-center gap-1.5 rounded-full bg-[#ecfdf5] px-2.5 py-1 text-sm font-semibold text-[#067647] ring-1 ring-[#067647]/15")}>
            {lead ? (
              <>
                <span aria-hidden="true" className={compactClassName("text-base font-bold leading-none")}>{lead[1]}</span>{' '}
              </>
            ) : (
              <TrendingDown aria-hidden="true" className="size-4" />
            )}
            {spread}
          </p>
        </div>
      </div>
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v)
          setPicked(true)
        }}
        orientation="vertical"
        className="gap-0!"
      >
        <div className={compactClassName("px-5 pt-5 md:px-6")}>
          <div className={compactClassName("flex flex-wrap items-center justify-between gap-2")}><h3 id={headId} className={compactClassName("text-base font-semibold tracking-tight text-neutral-950")}>{m.bestLabel}</h3><RotationControls label="Price comparison products" paused={picked} reduced={reduced} onPause={() => setPicked((v) => !v)} onMove={move} /></div>
          <TabsList aria-labelledby={headId} className="mt-3 h-auto! w-full! flex-col! items-stretch! justify-start! gap-1 rounded-xl! bg-[#f3f8ff]! p-1.5!">
            {m.items.map((it, k) => {
              const b = bestRow(it.rows)
              return (
                <TabsTrigger
                  key={it.compare.href}
                  value={String(k)}
                  className="group h-auto! w-full flex-none! justify-start! gap-3! whitespace-normal! rounded-lg! px-3! py-2.5! text-left hover:bg-white/70 data-[state=active]:border-[#0469f0]/30! data-[state=active]:bg-white!"
                >
                  <span aria-hidden="true" className={compactClassName("flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-neutral-600 ring-1 ring-black/10 transition-colors group-data-[state=active]:bg-[#ff9951] group-data-[state=active]:text-[#1a1a1a] group-data-[state=active]:ring-[#ff9951]")}>
                    {k + 1}
                  </span>
                  <span className={compactClassName("min-w-0 flex-1")}>
                    <span className={compactClassName("block text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-600")}>{it.brand}</span>
                    <span className={compactClassName("block truncate text-[13px] font-medium text-neutral-900")}>{it.title}</span>
                  </span>
                  {b ? <span className={compactClassName("tabular shrink-0 text-sm font-bold text-neutral-950")}>{b.price}{' '}</span> : null}
                </TabsTrigger>
              )
            })}
          </TabsList>
        </div>
        {m.items.map((it, k) => {
          const b = bestRow(it.rows)
          return (
            <TabsContent key={it.compare.href} value={String(k)} data-motion className="animate-v3-rise data-[state=inactive]:hidden">
              <p className={compactClassName("px-5 pt-4 text-sm font-semibold leading-snug text-neutral-900 md:px-6")}>
                <span className={compactClassName("text-[#0454c0]")}>{it.brand}</span> {it.title}
              </p>
              <ul className={compactClassName("mt-3 divide-y divide-neutral-100 border-y border-neutral-100")}>
                {it.rows.map((r) => {
                  const low = r === b
                  return (
                    <li key={r.store} className={compactClassName(cn('flex items-center gap-3 px-5 py-2.5 text-sm md:px-6', low && 'bg-[#f0faf4]'))}>
                      <RetailerMark name={r.store} />
                      <span className={compactClassName(cn('flex-1 text-neutral-800', low ? 'font-semibold' : 'font-medium'))}>{r.store}</span>
                      {low ? <Badge variant="outline" className="border-[#067647]/20! bg-white! text-[#067647]!">Lowest</Badge> : null}
                      <span className={compactClassName(cn('tabular text-neutral-950', low ? 'font-bold' : 'font-semibold'))} title={r.checked}>
                        {r.price}
                      </span>
                    </li>
                  )
                })}
              </ul>
              <div className={compactClassName("flex justify-end px-5 py-4 md:px-6")}>
                <Button asChild variant="ctaSecondary" className="rounded-full!">
                  <DestinationLink href={it.compare.href} aria-label={it.compare.aria}>
                    {words(it.compare.text)}
                    <ArrowRight aria-hidden="true" />
                  </DestinationLink>
                </Button>
              </div>
            </TabsContent>
          )
        })}
      </Tabs>
    </Card>
  )
}

/* ------------------------------------------------------------------ (2) the four data cards */

// `tint` colours the icon tile; `stat` colours v2's extra stat line (dark enough for 4.5:1 on its tint).
const DATA_CARD: { re: RegExp; I: LucideIcon; tint: string; stat: string }[] = [
  { re: /recall/i, I: ShieldAlert, tint: 'bg-[#fef3f2] text-[#b42318] ring-[#b42318]/15', stat: 'bg-[#fef3f2] text-[#b42318]' },
  { re: /rebate/i, I: BadgeDollarSign, tint: 'bg-[#ecfdf5] text-[#067647] ring-[#067647]/15', stat: 'bg-[#ecfdf5] text-[#067647]' },
  { re: /energy/i, I: Leaf, tint: 'bg-[#f0faf4] text-[#0e8a4d] ring-[#0e8a4d]/15', stat: 'bg-[#f0faf4] text-[#05603a]' },
]
const dataCard = (t: string) =>
  DATA_CARD.find((d) => d.re.test(t)) ?? { I: ChartLine, tint: 'bg-[#eef5ff] text-[#0469f0] ring-[#0469f0]/15', stat: 'bg-[#eef5ff] text-[#0454c0]' }

function DataCards({ cards }: { cards: Model['intel']['cards'] }) {
  return (
    <ul className={compactClassName("grid gap-4 sm:grid-cols-2 lg:grid-cols-1")}>
      {cards.map((c) => {
        const { I, tint, stat } = dataCard(c.title)
        return (
          <li key={c.title}>
            <Card className="group h-full gap-0! py-0! transition duration-200 hover:-translate-y-0.5 hover:shadow-md">
              <div className={compactClassName("flex h-full gap-4 p-5")}>
                <span aria-hidden="true" className={compactClassName(cn('flex size-11 shrink-0 items-center justify-center rounded-xl ring-1', tint))}>
                  <I className="size-5" />
                </span>
                <div className={compactClassName("flex min-w-0 flex-1 flex-col")}>
                  <h3 className={compactClassName("text-base font-semibold tracking-tight text-neutral-950")}>{c.title}</h3>
                  <p className={compactClassName("mt-1 text-pretty text-sm leading-relaxed text-neutral-600")}>{c.text}</p>
                  {c.stat ? (
                    <p className={compactClassName(cn('mt-2.5 inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 text-xs font-bold', stat))}>
                      <span aria-hidden="true" className={compactClassName("size-1.5 shrink-0 rounded-full bg-current")} />
                      {c.stat}
                    </p>
                  ) : null}
                  <DestinationLink
                    href={c.link.href}
                    aria-label={c.link.aria}
                    className="mt-auto inline-flex items-center gap-1.5 self-start pt-3 text-sm font-semibold text-[#0454c0] hover:underline hover:underline-offset-2"
                  >
                    {words(c.link.text)}
                    <ArrowRight aria-hidden="true" className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </DestinationLink>
                </div>
              </div>
            </Card>
          </li>
        )
      })}
    </ul>
  )
}

/* ------------------------------------------------------------------ (3) the price-history chart */

// The chart drawing (recharts) lives in IntelChart.tsx and loads as its own file once the page is interactive.
const PriceArea = React.lazy(() => import('./IntelChart'))

/** A 1, 2 or 5 × 10ⁿ step, so the price axis gets about four round gridlines. */
function niceStep(raw: number) {
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1e-9))))
  const n = raw / pow
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow
}

function prepChart(c: Model['intel']['chart']): ChartData | null {
  if (!c.series.length) return null
  const t0 = isoMs(c.series[0][0])
  // The "vs. typical" words come from the model's own daily table; the formula (v2's) only covers a missing row.
  const vsOf = (date: string, cents: number) => {
    const row = c.rows.find((r) => r[0] === date)
    if (row?.[2]) return row[2]
    const q = Math.round(100 * (1 - cents / c.typical))
    return q > 0 ? `${q}% under typical` : q === 0 ? 'at its typical price' : `${-q}% over typical`
  }
  const pts: Pt[] = c.series.map(([iso, cents], i) => {
    const date = mdy(iso)
    return { i, iso, day: Math.round((isoMs(iso) - t0) / DAY), date, cents, price: cents / 100, vs: vsOf(date, cents) }
  })
  const span = Math.max(1, pts[pts.length - 1].day)
  const xTicks: number[] = []
  for (let d = 0; d <= span; d += 7) xTicks.push(d)
  const prices = pts.map((p) => p.price).concat(c.typical / 100)
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  const step = niceStep(Math.max(1, ((max - min) * 1.5) / 4))
  const lo = Math.max(0, Math.floor((min - step / 2) / step) * step)
  const hi = Math.ceil((max + step / 2) / step) * step
  const yTicks: number[] = []
  for (let v = lo; v <= hi + 1e-9; v += step) yTicks.push(v)
  const tick = (d: number) => {
    const x = new Date(t0 + d * DAY)
    return `${String(x.getUTCMonth() + 1).padStart(2, '0')}/${String(x.getUTCDate()).padStart(2, '0')}`
  }
  return { pts, span, xTicks, lo, hi, yTicks, tick }
}

const KEYS: Record<string, (at: number, last: number) => number> = {
  ArrowLeft: (a) => a - 1,
  ArrowDown: (a) => a - 1,
  ArrowRight: (a) => a + 1,
  ArrowUp: (a) => a + 1,
  PageDown: (a) => a - 7,
  PageUp: (a) => a + 7,
  Home: () => 0,
  End: (_a, last) => last,
}

function PriceChart({ c }: { c: Model['intel']['chart'] }) {
  const [range, setRange] = React.useState(0)
  const [mode, setMode] = React.useState<'line' | 'bars'>('line')
  const [showTypical, setShowTypical] = React.useState(true)
  const data = React.useMemo(() => {
    const cutoff = isoMs(c.series[c.series.length - 1]?.[0] ?? '') - (range - 1) * DAY
    return prepChart({ ...c, series: range ? c.series.filter(([iso]) => isoMs(iso) >= cutoff) : c.series })
  }, [c, range])
  const reduced = useReducedMotion()
  const client = useIsClient()
  const n = data?.pts.length ?? 0
  const [at, setAt] = React.useState(100000)
  const active = Math.max(0, Math.min(at, n - 1))
  const helpId = React.useId()
  const label = c.thead[1] ?? ''
  const config = React.useMemo<ChartConfig>(() => ({ price: { label, color: '#0469f0' } }), [label])
  const pt = data?.pts[active]
  const last = data?.pts[n - 1]
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const go = KEYS[e.key]
    if (!go) return
    e.preventDefault()
    setAt(Math.max(0, Math.min(n - 1, go(active, n - 1))))
  }
  return (
    <Card data-price-chart className="mt-8 gap-0! overflow-hidden py-0!">
      <div className={compactClassName("flex flex-col gap-5 p-5 md:flex-row md:items-start md:justify-between md:p-6")}>
        <div className={compactClassName("max-w-3xl")}>
          <h3 className={compactClassName("text-balance text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>{c.title}</h3>
          <p className={compactClassName("mt-2 text-pretty text-sm leading-relaxed text-neutral-600 md:text-base")}>{c.sub}</p>
        </div>
        <DestinationLink href={c.link.href} className="shrink-0 rounded-2xl border border-[#cbe3d7] bg-[#f1faf5] px-4 py-3 md:text-right">
          <p className={compactClassName("text-xs font-semibold uppercase tracking-wider text-[#067647]")}>Latest observed price</p>
          <p className={compactClassName("tabular text-3xl font-bold tracking-tight text-neutral-950")}>{c.now}</p>
          <p className={compactClassName("mt-1 text-xs font-semibold text-[#0454c0]")}>Compare store offers →</p>
        </DestinationLink>
      </div>
      {data && pt && last ? (
        <div className={compactClassName("px-3 md:px-5")}>
          <div className={compactClassName("v3-chart-controls")}>
            <div role="group" aria-label="Price history range">
              {[[7, '7 days'], [14, '14 days'], [30, '30 days'], [0, 'All history']].map(([value, text]) => <button key={value} type="button" aria-pressed={range === value} onClick={() => { setRange(Number(value)); setAt(100000) }}>{text}</button>)}
            </div>
            <div role="group" aria-label="Chart style">
              <button type="button" aria-pressed={mode === 'line'} onClick={() => setMode('line')}>Price line</button>
              <button type="button" aria-pressed={mode === 'bars'} onClick={() => setMode('bars')}>Daily bars</button>
            </div>
            <button type="button" aria-pressed={showTypical} onClick={() => setShowTypical((v) => !v)}>Typical price</button>
          </div>
          <div className={compactClassName("v3-chart-readout")} aria-live="polite" aria-atomic="true">
            <span>{pt.date}</span><strong>{usd(pt.cents)}</strong>
            <span className={compactClassName(pt.cents < c.typical ? 'text-[#067647]' : 'text-neutral-600')}>{pt.vs}</span>
            {showTypical ? <span className={compactClassName("v3-chart-legend")}><i aria-hidden="true" />60-day typical {usd(c.typical)}</span> : null}
          </div>
          <div role="slider" tabIndex={0} aria-label={c.aria} aria-valuemin={0} aria-valuemax={n - 1} aria-valuenow={active} aria-valuetext={`${pt.date}: ${usd(pt.cents)}`} aria-describedby={helpId} onKeyDown={onKeyDown} className={compactClassName("h-[230px] w-full cursor-crosshair rounded-xl md:h-[280px]")}>
            {client ? <React.Suspense fallback={null}><PriceArea data={data} config={config} label={label} reduced={reduced} typical={c.typical} usd={usd} pt={pt} last={last} show={true} onMove={setAt} mode={mode} showTypical={showTypical} /></React.Suspense> : null}
          </div>
          <label htmlFor={`${helpId}-range`} className={compactClassName("v3-chart-scrubber")}><span>Explore each recorded price</span><input id={`${helpId}-range`} type="range" min={0} max={n - 1} step={1} value={active} onChange={(e) => setAt(Number(e.target.value))} aria-label="Explore price history" aria-valuetext={`${pt.date}: ${usd(pt.cents)}`} /></label>
          <div className={compactClassName("v3-chart-jumps")}>
            <button type="button" onClick={() => setAt(data.pts.reduce((best, p, i, list) => p.cents < list[best].cents ? i : best, 0))}>Lowest in range</button>
            <button type="button" onClick={() => setAt(data.pts.reduce((best, p, i, list) => p.cents > list[best].cents ? i : best, 0))}>Highest in range</button>
            <button type="button" onClick={() => setAt(n - 1)}>Latest observation</button>
          </div>
        </div>
      ) : null}
      <div className={compactClassName("flex flex-col gap-3 px-5 pt-3 pb-5 sm:flex-row sm:items-center sm:justify-between md:px-6")}>
        <p id={helpId} className={compactClassName("text-sm text-neutral-600")}>Tap a price or drag the slider. Arrow keys explore each observation.</p>
        <DestinationLink href={c.link.href} aria-label={c.link.aria} className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#0454c0] hover:underline">{words(c.link.text)}<ArrowRight aria-hidden="true" className="size-4" /></DestinationLink>
      </div>
      <Collapsible className="border-t border-neutral-100">
        <CollapsibleTrigger className="group flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-sm font-semibold text-neutral-900 transition-colors hover:bg-[#f8fbff] md:px-6">
          {c.tableSummary}
          <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-[#0469f0] transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent forceMount className="px-5 pb-5 data-[state=closed]:hidden md:px-6">
          <div className={compactClassName("overflow-hidden rounded-xl border border-neutral-200")}>
            <ScrollTable label={c.caption} className="max-h-[26rem] overflow-y-auto">
              <TableCaption className="sr-only">{c.caption}</TableCaption>
              <TableHeader className="sticky top-0 z-10 bg-[#f3f8ff]">
                <TableRow className="hover:bg-transparent!">
                  {c.thead.map((h) => (
                    <TableHead key={h} scope="col" className="h-10! px-4! text-xs font-semibold! uppercase tracking-[0.06em] text-neutral-600!">
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.rows.map((r) => (
                  <TableRow key={r[0]} className="hover:bg-[#f8fbff]!">
                    <TableHead scope="row" className="h-auto! px-4! py-2.5 font-medium tabular text-neutral-900!">
                      {r[0]}
                    </TableHead>
                    {r.slice(1).map((v, j) => (
                      <TableCell key={j} className={cn('px-4! py-2.5! tabular', j === 0 ? 'font-semibold text-neutral-950' : /under/.test(v) ? 'text-[#067647]' : 'text-neutral-600')}>
                        {v}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

/* ------------------------------------------------------------------ (4) prices by type */

function PricesByType({ p }: { p: Model['intel']['prices'] }) {
  return (
    <Card className="mt-8 gap-0! overflow-hidden py-0!">
      <div className={compactClassName("flex flex-col gap-3 p-5 md:p-6")}>
        <div className={compactClassName("flex flex-wrap items-center justify-between gap-3")}>
          <h3 className={compactClassName("text-balance text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>{p.title}</h3>
          <DailyDate source={p.stamp} />
        </div>
        <p className={compactClassName("max-w-3xl text-pretty text-sm leading-relaxed text-neutral-600 md:text-base")}>{p.text}</p>
      </div>
      <div className={compactClassName("px-5 pb-5 md:px-6 md:pb-6")}>
        <TableScrollHint />
        <div className={compactClassName("overflow-hidden rounded-xl border border-neutral-200")}>
          <ScrollTable label={p.caption}>
            <TableCaption className="sr-only">{p.caption}</TableCaption>
            <TableHeader className="bg-[#f3f8ff]">
              <TableRow className="hover:bg-transparent!">
                {p.thead.map((h, k) => (
                  <TableHead key={h} scope="col" className={cn('h-11! px-4! text-xs font-semibold! uppercase tracking-[0.06em] text-neutral-600!', k > 0 && 'text-right!')}>
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {p.rows.map((r, i) => {
                const l = p.rowLinks[i]
                return (
                  <TableRow key={r[0]} className="hover:bg-[#f8fbff]!">
                    <TableHead scope="row" className="h-auto! px-4! py-3 font-medium text-neutral-900!">
                      {l ? (
                        <DestinationLink href={l.href} aria-label={l.aria} className="text-[#0454c0] hover:underline hover:underline-offset-2">
                          {r[0]}
                        </DestinationLink>
                      ) : (
                        r[0]
                      )}
                    </TableHead>
                    {r.slice(1).map((v, j) => (
                      <TableCell key={j} className={cn('px-4! py-3! text-right tabular', j === 1 ? 'font-bold text-neutral-950' : 'text-neutral-700')}>
                        {v}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </ScrollTable>
        </div>
      </div>
    </Card>
  )
}

/* ------------------------------------------------------------------ price intelligence (V16) */

export function Intel({ model }: { model: Model }) {
  const m = model.intel
  return (
    <Section aria={m.aria} v2="V16" tone="sky">
      <SectionHeader head={m.head} tone="sky" />
      <div className={compactClassName("grid gap-6 lg:grid-cols-[1.1fr_1fr] lg:items-start")}>
        <LiveCheck m={m.live} />
        <DataCards cards={m.cards} />
      </div>
      <ProductRail {...model.discoveryRails.compactFreezers} className="v3-discovery-rail" />
      <PriceChart c={m.chart} />
      <PricesByType p={m.prices} />
      <ProductRail {...model.discoveryRails.columnFreezers} className="v3-discovery-rail" />
    </Section>
  )
}

/* ------------------------------------------------------------------ never overpay (V16b) */

export function Overpay({ model }: { model: Model }) {
  const m = model.overpay
  return (
    <Section aria={m.aria} v2="V16b" tone="white" className="py-8! md:py-10!">
      <div className={compactClassName("relative overflow-hidden rounded-[28px] border border-[#0469f0]/10 bg-gradient-to-br from-[#e9f2ff] via-[#f8fbff] to-[#fff0e3] px-6 py-10 sm:px-10 md:px-14 md:py-16")}>
        <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0")}>
          <div className={compactClassName("absolute -top-28 -left-24 size-[420px] rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.18),transparent_65%)]")} />
          <div className={compactClassName("absolute -right-28 -bottom-32 size-[460px] rounded-full bg-[radial-gradient(circle,rgba(255,153,81,0.26),transparent_65%)]")} />
          <svg viewBox="0 0 1200 160" preserveAspectRatio="none" className={compactClassName("absolute inset-x-0 bottom-0 h-28 w-full")}>
            <path d="M0 120 L150 120 L150 70 L330 70 L330 112 L520 112 L520 54 L700 54 L700 104 L880 104 L880 40 L1050 40 L1050 92 L1200 92" fill="none" stroke="#0469f0" strokeOpacity="0.14" strokeWidth="3" />
          </svg>
        </div>
        <div className={compactClassName("relative grid gap-8 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-10")}>
          <div>
            <p className={compactClassName("inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#0454c0] shadow-xs ring-1 ring-[#0469f0]/15")}>
              <LiveDot />
              {m.eyebrow}
            </p>
            <h2 className={compactClassName("mt-5 text-balance text-4xl font-bold leading-[1.02] tracking-tight text-neutral-950 sm:text-5xl lg:text-6xl")}>
              <span className={compactClassName("bg-gradient-to-r from-[#0469f0] to-[#004aad] bg-clip-text text-transparent")}>{m.title}</span>
            </h2>
          </div>
          <div>
            <ul className={compactClassName("grid gap-3")}>
              {m.lines.map((l) => (
                <li key={l} className={compactClassName("flex items-start gap-3 rounded-2xl bg-white/85 p-4 shadow-xs ring-1 ring-black/5 backdrop-blur")}>
                  <CircleCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-[#10a65c]" />
                  <span className={compactClassName("text-pretty text-[15px] font-medium leading-snug text-neutral-800")}>{l}</span>
                </li>
              ))}
            </ul>
            <p className={compactClassName("mt-5 inline-flex items-center gap-2 rounded-full bg-[#ff9951] px-4 py-2 text-sm font-semibold text-[#1a1a1a] shadow-[0_10px_24px_-12px_rgba(255,153,81,0.9)]")}>
              <Eye aria-hidden="true" className="size-4" />
              {m.tagline}
            </p>
          </div>
        </div>
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ fridge finder quiz (V17a) */

// The live site's bucket rule (Repo components/homepage-2026/quiz.tsx, bucketFor), unchanged.
// a[n] is the 1-based option chosen for question n.
function bucketFor(a: Record<number, number>) {
  if (a[3] === 2) return 'counter'
  if (a[2] === 4 || a[1] === 1 || a[4] === 1) return 'compact'
  if (a[3] === 4) return 'garage'
  if (a[4] === 2) return 'value'
  if (a[2] === 2) return 'sxs'
  return 'french'
}
const pad2 = (n: number) => String(n).padStart(2, '0')

function QuizCard({ m, id }: { m: Model['quiz']; id: string }) {
  const [answers, setAnswers] = React.useState<Record<number, number>>({})
  const total = m.questions.length
  const answered = Object.keys(answers).length
  const done = total > 0 && answered >= total
  const step = Math.min(answered + 1, total)
  const pct = done ? 100 : Math.round((step / Math.max(total, 1)) * 100)
  const key = done ? bucketFor(answers) : null
  const bucket = key ? m.buckets.find((b) => b.key === key) : undefined
  const q = m.questions[step - 1]
  const labelId = React.useId()
  const qId = React.useId()
  const heading = React.useRef<HTMLHeadingElement>(null)
  const prev = React.useRef(answered)
  // Move focus to the new question (or the results) whenever the step changes, never on first render.
  React.useEffect(() => {
    if (prev.current !== answered) heading.current?.focus()
    prev.current = answered
  }, [answered])
  const back = () => {
    for (let k = total; k >= 1; k--) {
      if (answers[k]) {
        const next = { ...answers }
        delete next[k]
        setAnswers(next)
        return
      }
    }
  }
  return (
    <Card id={id} tabIndex={-1} className="scroll-mt-28 gap-0! overflow-hidden py-0! shadow-[0_30px_70px_-38px_rgba(255,153,81,0.75)]! ring-1 ring-[#ff9951]/20 lg:sticky lg:top-28">
      <div aria-hidden="true" className={compactClassName("h-1.5 bg-gradient-to-r from-[#ff9951] via-[#ffc29a] to-[#0469f0]")} />
      <div className={compactClassName("p-5 sm:p-7")}>
        <div className={compactClassName("flex flex-wrap items-center justify-between gap-2")}>
          <div className={compactClassName("flex min-h-8 items-center gap-3")}>
            {answered > 0 ? (
              <Button type="button" variant="outline" size="sm" onClick={back} className="rounded-full! text-neutral-700">
                <ArrowLeft aria-hidden="true" />
                Back
              </Button>
            ) : null}
            <span id={labelId} className={compactClassName("font-mono text-xs font-semibold tracking-[0.14em] text-neutral-900")}>
              {done ? 'YOUR MATCHES' : `QUESTION ${pad2(step)} / ${pad2(total)}`}
            </span>
          </div>
          <span aria-hidden="true" className={compactClassName("tabular text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-500")}>{`${pct}% complete`}</span>
        </div>
        {/* Repo's shadcn Progress keeps `value` for the bar's width but never hands it to the Radix root, which then reads
            as indeterminate; the ARIA values are passed here so the bar announces "QUESTION 0N / 05, 20% complete". */}
        <Progress
          value={pct}
          aria-valuenow={pct}
          aria-valuetext={`${pct}% complete`}
          aria-labelledby={labelId}
          className="mt-3 h-1.5! bg-neutral-200! *:data-[slot=progress-indicator]:bg-[#ff9951]!"
        />
        {!done && q ? (
          <div className={compactClassName("mt-6")}>
            <h3 ref={heading} id={qId} tabIndex={-1} className={compactClassName("scroll-mt-28 text-balance text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>
              {q.q}
            </h3>
            <p className={compactClassName("mt-1.5 text-pretty text-sm leading-relaxed text-neutral-600")}>{q.sub}</p>
            <ToggleGroup
              key={step}
              type="single"
              value={answers[step] ? String(answers[step]) : ''}
              onValueChange={(v) => {
                const pick = v ? Number(v) : answers[step]
                if (pick) setAnswers({ ...answers, [step]: pick })
              }}
              aria-labelledby={qId}
              className="mt-5 grid! w-full! grid-cols-1 items-stretch! gap-3 sm:grid-cols-2"
            >
              {q.options.map(([label, sub], i) => (
                <ToggleGroupItem
                  key={label}
                  value={String(i + 1)}
                  className="h-auto! min-h-[76px] flex-col! items-start! justify-center! gap-1! whitespace-normal! rounded-xl! border border-neutral-200 bg-white! px-4! py-3.5! text-left shadow-xs! transition! hover:-translate-y-0.5 hover:border-[#ff9951] hover:bg-[#fff7f0]! hover:text-neutral-950! data-[state=on]:border-[#ff9951] data-[state=on]:bg-[#fff1e6]! data-[state=on]:text-neutral-950!"
                >
                  <span className={compactClassName("text-[15px] font-semibold leading-snug text-neutral-950")}>{label}</span>
                  {sub ? <span className={compactClassName("font-mono text-xs text-neutral-500")}>{sub}</span> : null}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        ) : (
          <div className={compactClassName("mt-6")}>
            <h3 ref={heading} tabIndex={-1} className={compactClassName("scroll-mt-28 text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>
              Explore this product group
            </h3>
            <p className={compactClassName("mt-1.5 text-pretty text-sm leading-relaxed text-neutral-600")}>
              Your household, style, location and budget answers choose a product group. These are examples from that group, with prices where available. Check dimensions and features on each product page.
            </p>
            {key && bucket ? (
              <>
                <p data-motion className={compactClassName("mt-5 animate-v3-rise text-xs font-bold uppercase tracking-[0.08em] text-[#0454c0]")}>
                  {m.bucketLabel[key] ?? bucket.label}
                </p>
                <ul className={compactClassName("mt-2 grid gap-2")}>
                  {bucket.products.map((p, i) => (
                    <li key={p.fin} data-motion className={compactClassName("animate-v3-rise")} style={{ animationDelay: `${80 + i * 70}ms` }}>
                      <ProductRow p={shopItem(p)} />
                    </li>
                  ))}
                </ul>
                <DestinationLink
                  href={bucket.href}
                  data-motion
                  className="mt-3 inline-flex animate-v3-rise items-center gap-1.5 text-sm font-semibold text-[#0454c0] [animation-delay:320ms] hover:underline hover:underline-offset-2"
                >
                  {m.bucketAll[key] ?? bucket.label}
                  <ArrowRight aria-hidden="true" className="size-4" />
                </DestinationLink>
              </>
            ) : null}
            <div className={compactClassName("mt-5")}>
              <Button type="button" variant="outline" size="sm" onClick={() => setAnswers({})} className="rounded-full! text-neutral-700">
                <RotateCcw aria-hidden="true" />
                Start over
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

export function Quiz({ model }: { model: Model }) {
  const m = model.quiz
  const cardId = m.cta.href.replace(/^#/, '') || 'quiz-questions'
  return (
    <Section id={m.id} aria={m.aria} v2="V17a" tone="peach">
      <div className={compactClassName("grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-10")}>
        <div className={compactClassName("flex flex-col items-start")}>
          <Badge variant="orange" className="rounded-full! px-3! py-1! font-mono text-[11px]! font-bold! tracking-[0.24em]">
            {m.mono}
          </Badge>
          <h2 className={compactClassName("mt-4 text-balance text-3xl font-bold tracking-tight text-neutral-950 md:text-[2.5rem] md:leading-[1.1]")}>{m.title}</h2>
          <p className={compactClassName("mt-3 max-w-xl text-pretty text-base leading-relaxed text-neutral-600 md:text-lg")}>{m.text}</p>
          <p className={compactClassName("mt-4 inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium text-neutral-700 shadow-xs ring-1 ring-black/5")}>
            <ListChecks aria-hidden="true" className="size-4 shrink-0 text-[#0469f0]" />
            {m.metaRaw || m.meta.join(' · ')}
          </p>
          <div className={compactClassName("mt-6 flex flex-wrap items-center gap-x-4 gap-y-3")}>
            <Button asChild variant="ctaPrimary" size="lg" className="h-12! rounded-full! px-7! text-[15px]! font-semibold!">
              <DestinationLink href={m.cta.href} aria-label={m.cta.aria}>
                {words(m.cta.text)}
                <ArrowRight aria-hidden="true" />
              </DestinationLink>
            </Button>
            <p className={compactClassName("text-sm text-neutral-600")}>{m.note}</p>
          </div>
          <div className={compactClassName("group relative mt-8 aspect-[16/10] w-full overflow-hidden rounded-2xl shadow-[0_24px_50px_-30px_rgba(180,83,9,0.45)] ring-1 ring-black/5")}>
            <Img src={m.img.src} alt={m.img.alt} fit="cover" className="size-full transition duration-500 group-hover:scale-[1.04]" />
          </div>
        </div>
        <div>
          <QuizCard m={m} id={cardId} />
        </div>
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ free tools (V17) */

const TOOL: { re: RegExp; I: LucideIcon }[] = [
  { re: /\bsize\b/i, I: Ruler },
  { re: /\benergy\b/i, I: Zap },
  { re: /\bfind\b/i, I: SlidersHorizontal },
  { re: /\bspace\b/i, I: LayoutPanelTop },
  { re: /\blifespan\b/i, I: Timer },
  { re: /\brecall\b/i, I: ShieldAlert },
  { re: /\brebate\b/i, I: BadgeDollarSign },
  { re: /\brepair\b/i, I: Wrench },
  { re: /\bage\b/i, I: Hourglass },
  { re: /\bfreezer\b/i, I: Snowflake },
]
// Each tool has a distinct visual cue; the shared blue ink ties the collection together.
const toolIcon = (title: string) => TOOL.find((t) => t.re.test(title)) ?? { I: Calculator }

export function Tools({ model }: { model: Model }) {
  const m = model.tools
  return (
    <Section aria={m.aria} v2="V17" tone="mist">
      <SectionHeader head={m.head} tone="mist" />
      <ul className={compactClassName("grid gap-3 sm:grid-cols-2 lg:grid-cols-5")}>
        {m.cards.map((c) => {
          const { I } = toolIcon(c.title)
          return (
            <li key={c.href}>
              <Item
                asChild
                variant="outline"
                className="h-full bg-white transition! duration-200! hover:-translate-y-0.5 hover:border-[#0469f0]/35! hover:bg-white! hover:shadow-md lg:flex-col! lg:items-start! lg:gap-3!"
              >
                <DestinationLink href={c.href}>
                  <ItemMedia variant="icon" className="size-11! rounded-xl! border-0! bg-[#eaf2ff]! text-[#0454c0]">
                    <I aria-hidden="true" className="size-5" />
                  </ItemMedia>
                  <ItemContent className="min-w-0">
                    <ItemTitle className="text-[15px]! font-semibold! text-neutral-950">{c.title}</ItemTitle>
                    <ItemDescription className="text-[13px]!">{c.text}</ItemDescription>
                  </ItemContent>
                </DestinationLink>
              </Item>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}
